// ─────────────────────────────────────────────────────────────────────────────
// RexyGraphViewport — the frame the galaxy sits in.
//
// It owns the graph's OWN background and nothing else: #02050B, a faint deep-navy
// radial, and a vignette. The app's AtmosphereBackground is NOT reused here and
// is not touched — the graph lab is a separate surface and the homepage console
// grid must never bleed into it. The visual density in this frame comes from the
// graph itself, never from decorative dust behind it.
//
// The pointer rules live here as well as on the canvas host: while the user is
// manipulating the graph the page must not scroll, rubber-band, or start a text
// selection.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { GRAPH_BACKGROUND_HEX } from './graphPalette';

export interface RexyGraphViewportProps {
  children: React.ReactNode;
  /** Rendered above the canvas — dev stats in G0; filters/inspector from G1. */
  overlay?: React.ReactNode;
}

export default function RexyGraphViewport({
  children,
  overlay,
}: RexyGraphViewportProps): React.ReactElement {
  return (
    <div style={rootStyle}>
      <div style={atmosphereStyle} />
      <div style={canvasSlotStyle}>{children}</div>
      <div style={vignetteStyle} />
      {overlay ? <div style={overlaySlotStyle}>{overlay}</div> : null}
    </div>
  );
}

const rootStyle: React.CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  background: GRAPH_BACKGROUND_HEX,
  touchAction: 'none',
  overscrollBehavior: 'contain',
  userSelect: 'none',
  WebkitUserSelect: 'none',
};

// Faint deep-navy lift behind the graph. Low enough that a dim leaf node still
// reads against it — the background must never compete with the data.
const atmosphereStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 0,
  pointerEvents: 'none',
  background:
    'radial-gradient(ellipse at 50% 46%, rgba(18,44,86,0.55) 0%, rgba(6,14,32,0.35) 42%, rgba(2,5,11,1) 100%)',
};

const canvasSlotStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 1,
};

const vignetteStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  pointerEvents: 'none',
  background: 'radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)',
};

const overlaySlotStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 3,
  // The overlay is a frame for controls; clicks fall through to the graph unless
  // a control itself opts back in.
  pointerEvents: 'none',
};
